import { useApolloClient } from "@apollo/client";
import { Edit as EditIcon } from "@mui/icons-material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, Button, Stack, Typography } from "@mui/material";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import { TFnType } from "@msys/tolgee";
import { useSnackbar } from "notistack";
import React from "react";
import { useTranslate } from "@tolgee/react";
import { namedOperations, ProjectType } from "../../../clients/graphqlTypes.js";
import { Page, PageTopbarItem } from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { ConfirmModal } from "../../commons/modals/ConfirmModal.js";
import {
  buildCategoryTree,
  type CategoryWithMatchAndChildren,
} from "../../features/categories/helpers.js";
import { useCategories } from "../../features/categories/useCategories.js";
import {
  matrixCellKey,
  OfferedServicesMatrix,
  parseMatrixCellKey,
  PROJECT_TYPE_ORDER,
} from "../../features/organisations/offeredServices/OfferedServicesMatrix.js";
import { useProjectTypes } from "../../features/projects/useProjectTypes.js";
import {
  useOrganisationSettingsOfferedServicesQuery,
  useSetOrganisationOfferedServicesMutation,
} from "./OrganisationSettingsOfferedServices.generated.js";

const REFETCH = {
  refetchQueries: [namedOperations.Query.OrganisationSettingsOfferedServices],
  awaitRefetchQueries: true,
};

type ViewTreeNode = {
  id: string;
  name: string;
  rank: number;
  declared: boolean;
  projectTypes: ProjectType[];
  children: ViewTreeNode[];
};

interface Props {
  submenuItems: PageTopbarItem[];
}

export const OrganisationSettingsOfferedServices = ({
  submenuItems,
}: Props) => {
  const { t } = useTranslate([
    "OrganisationPageTopbar",
    "OrganisationProfile",
    "Global",
  ]);
  const client = useApolloClient();
  const { enqueueSnackbar } = useSnackbar();

  const query = useOrganisationSettingsOfferedServicesQuery({
    client,
    fetchPolicy: "cache-and-network",
  });
  const organisation = query.data?.viewer?.organisation;

  const [setServices, { loading: saving }] =
    useSetOrganisationOfferedServicesMutation({
      client,
      ...REFETCH,
    });

  const { categories: allCategories } = useCategories();
  const { projectTypeLabels } = useProjectTypes();

  const declaredIds = React.useMemo(
    () => new Set(organisation?.categories ?? []),
    [organisation?.categories]
  );

  const [mode, setMode] = React.useState<"view" | "edit">("view");
  const [pendingDeclared, setPendingDeclared] = React.useState<Set<string>>(
    new Set()
  );
  const [pendingSelections, setPendingSelections] = React.useState<Set<string>>(
    new Set()
  );
  // Nodes the user has collapsed; everything else in the view tree stays open.
  const [viewCollapsed, setViewCollapsed] = React.useState<Set<string>>(
    new Set()
  );
  const [confirmRemovalOpen, setConfirmRemovalOpen] = React.useState(false);

  const projectTypesByCategory = React.useMemo(() => {
    const map = new Map<string, Set<ProjectType>>();
    if (!organisation) return map;
    for (const node of organisation.offeredServices) {
      const existing = map.get(node.category.id);
      if (existing) existing.add(node.projectType);
      else map.set(node.category.id, new Set([node.projectType]));
    }
    return map;
  }, [organisation]);

  const orderedProjectTypes = React.useCallback(
    (categoryId: string): ProjectType[] => {
      const set = projectTypesByCategory.get(categoryId);
      if (!set) return [];
      return PROJECT_TYPE_ORDER.filter(pt => set.has(pt));
    },
    [projectTypesByCategory]
  );

  // Declared categories + their ancestor path. Non-declared ancestors are
  // included so the tree path renders, but flagged for grayed-out display.
  const viewTree = React.useMemo<ViewTreeNode[]>(() => {
    if (allCategories.length === 0 || declaredIds.size === 0) return [];
    const byId = new Map(allCategories.map(c => [c.id, c]));
    const visible = new Set<string>();
    for (const id of declaredIds) {
      let curId: string | null = id;
      while (curId && !visible.has(curId)) {
        visible.add(curId);
        curId = byId.get(curId)?.parentId ?? null;
      }
    }
    const filtered = allCategories
      .filter(c => visible.has(c.id))
      .map(c => ({ ...c, match: undefined }));
    const { categoriesTree } = buildCategoryTree(filtered);
    const toNode = (c: CategoryWithMatchAndChildren): ViewTreeNode => ({
      id: c.id,
      name: c.name,
      rank: c.rank,
      declared: declaredIds.has(c.id),
      projectTypes: orderedProjectTypes(c.id),
      children: (c.children ?? [])
        .slice()
        .sort((a, b) => a.rank - b.rank)
        .map(toNode),
    });
    return categoriesTree
      .slice()
      .sort((a, b) => a.rank - b.rank)
      .map(toNode);
  }, [allCategories, declaredIds, orderedProjectTypes]);

  // Default to fully expanded — the tree only contains declared + ancestors.
  // Honor any nodes the user has explicitly collapsed.
  const viewExpanded = React.useMemo(() => {
    const ids: string[] = [];
    const visit = (nodes: ViewTreeNode[]) => {
      for (const n of nodes) {
        if (!viewCollapsed.has(n.id)) ids.push(n.id);
        visit(n.children);
      }
    };
    visit(viewTree);
    return ids;
  }, [viewTree, viewCollapsed]);

  const declaredRootVerticalIds = React.useMemo(() => {
    if (allCategories.length === 0 || declaredIds.size === 0) {
      return new Set<string>();
    }
    const byId = new Map(allCategories.map(c => [c.id, c]));
    const roots = new Set<string>();
    for (const id of declaredIds) {
      let curId: string | null = id;
      while (curId) {
        const node = byId.get(curId);
        if (!node) break;
        if (!node.parentId) {
          roots.add(curId);
          break;
        }
        curId = node.parentId;
      }
    }
    return roots;
  }, [allCategories, declaredIds]);

  const enterEdit = () => {
    setPendingDeclared(new Set(declaredIds));
    const seedCells = new Set<string>();
    if (organisation) {
      for (const s of organisation.offeredServices) {
        seedCells.add(matrixCellKey(s.category.id, s.projectType));
      }
    }
    setPendingSelections(seedCells);
    setMode("edit");
  };

  const cancelEdit = () => setMode("view");

  const handleDeclaredChange = React.useCallback((next: readonly string[]) => {
    const nextSet = new Set(next);
    setPendingDeclared(nextSet);
    // Invariant: any cell implies its category is in `declared`.
    setPendingSelections(prev => {
      const filtered = new Set<string>();
      for (const key of prev) {
        if (nextSet.has(parseMatrixCellKey(key).categoryId)) filtered.add(key);
      }
      return filtered;
    });
  }, []);

  const handleToggleCell = React.useCallback(
    (categoryId: string, projectType: ProjectType) => {
      const key = matrixCellKey(categoryId, projectType);
      setPendingSelections(prev => {
        const copy = new Set(prev);
        if (copy.has(key)) copy.delete(key);
        else copy.add(key);
        return copy;
      });
    },
    []
  );

  // Removed categories whose project-type selections would be lost on save.
  // The user is asked to confirm before committing those losses.
  const removedWithServicesNames = React.useMemo(() => {
    if (!organisation) return [] as string[];
    if (mode !== "edit") return [] as string[];
    const byId = new Map(allCategories.map(c => [c.id, c]));
    const removedIds = Array.from(declaredIds).filter(
      id => !pendingDeclared.has(id)
    );
    const removedWithTypes = removedIds.filter(
      id => (projectTypesByCategory.get(id)?.size ?? 0) > 0
    );
    return removedWithTypes.map(id => byId.get(id)?.name ?? id);
  }, [
    organisation,
    mode,
    declaredIds,
    pendingDeclared,
    projectTypesByCategory,
    allCategories,
  ]);

  const executeSave = async () => {
    if (!organisation) return;
    try {
      const categories = Array.from(pendingDeclared);
      const services = Array.from(pendingSelections).map(parseMatrixCellKey);
      await setServices({
        variables: { input: { categories, services } },
      });
      setMode("view");
    } catch (error) {
      if (error instanceof Error) {
        enqueueSnackbar(error.message, { variant: "error" });
      }
    }
  };

  const handleSave = () => {
    if (removedWithServicesNames.length > 0) {
      setConfirmRemovalOpen(true);
    } else {
      void executeSave();
    }
  };

  return (
    <Page
      title={t("Offered services", { ns: "OrganisationPageTopbar" })}
      submenuItems={submenuItems}
    >
      {organisation && (
        <PageContainer>
          <Stack spacing={2}>
            <Box alignSelf={"flex-end"}>
              {mode === "view" ? (
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={enterEdit}
                >
                  {t("Edit", { ns: "Global" })}
                </Button>
              ) : (
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {t("Save", { ns: "Global" })}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={cancelEdit}
                    disabled={saving}
                  >
                    {t("Cancel", { ns: "Global" })}
                  </Button>
                </Stack>
              )}
            </Box>

            {mode === "view" && viewTree.length === 0 && (
              <Typography variant="body1" color="text.secondary">
                {t("No categories declared yet.", {
                  ns: "OrganisationProfile",
                })}
              </Typography>
            )}

            {mode === "view" && viewTree.length > 0 && (
              <SimpleTreeView
                expandedItems={viewExpanded}
                onExpandedItemsChange={(_, items) => {
                  const open = new Set(items);
                  setViewCollapsed(prev => {
                    const next = new Set(prev);
                    for (const id of viewExpanded) {
                      if (!open.has(id)) next.add(id);
                    }
                    for (const id of items) next.delete(id);
                    return next;
                  });
                }}
                expansionTrigger="iconContainer"
                disableSelection
              >
                {renderViewNodes(viewTree, { t, projectTypeLabels })}
              </SimpleTreeView>
            )}

            {mode === "edit" && (
              <OfferedServicesMatrix
                categories={allCategories}
                declaredCategoryIds={pendingDeclared}
                cellSelections={pendingSelections}
                onDeclaredChange={handleDeclaredChange}
                onToggleCell={handleToggleCell}
                initiallyExpandedRootIds={declaredRootVerticalIds}
              />
            )}
          </Stack>
        </PageContainer>
      )}
      {confirmRemovalOpen && (
        <ConfirmModal
          title={t("Remove category", { ns: "OrganisationProfile" })}
          text={t(
            "Removing {categories} will also remove all project-type selections within them. Continue?",
            {
              ns: "OrganisationProfile",
              categories: removedWithServicesNames.join(", "),
            }
          )}
          confirmButtonLabel={t("Remove", { ns: "Global" })}
          handleConfirm={executeSave}
          handleClose={() => setConfirmRemovalOpen(false)}
        />
      )}
    </Page>
  );
};

type ViewRenderOptions = {
  t: TFnType<"OrganisationProfile">;
  projectTypeLabels: Record<ProjectType, string>;
};

function renderViewNodes(
  nodes: ViewTreeNode[],
  options: ViewRenderOptions
): React.ReactNode {
  const { t, projectTypeLabels } = options;
  return nodes.map(node => (
    <TreeItem
      key={node.id}
      itemId={node.id}
      label={
        <Stack spacing={0.25} py={0.5}>
          <Typography
            variant="h4"
            color={node.declared ? "text.primary" : "text.disabled"}
          >
            {node.name}
          </Typography>
          {node.declared &&
            (node.projectTypes.length > 0 ? (
              <Typography variant="caption" color="secondary">
                {node.projectTypes.map(pt => projectTypeLabels[pt]).join(", ")}
              </Typography>
            ) : (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <WarningAmberIcon color="warning" fontSize="extra-small" />
                <Typography variant="caption" color="warning">
                  {t("No project type specified", {
                    ns: "OrganisationProfile",
                  })}
                </Typography>
              </Stack>
            ))}
        </Stack>
      }
    >
      {node.children.length > 0 && renderViewNodes(node.children, options)}
    </TreeItem>
  ));
}
